import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Autocomplete,
    CircularProgress,
    AutocompleteRenderInputParams,
    Box,
    TextField,
    Tooltip,
    Typography,
    TextFieldProps
} from '@mui/material';
import { ListValue, ListDataTypeEnum } from 'views/backoffice/CustomLists/types';
import { generateInlineObject } from 'ui-component/RecordView/PropertiesPanel/utils';
import { useTheme, Theme, SxProps } from '@mui/material/styles';
import { useLazyListValues } from './hooks/useLazyListValues';

interface LazyLoadingListProps {
    listId: number;
    listDataType: ListDataTypeEnum | undefined;
    selectedId?: string;
    onChange: (value: string | undefined | null) => void;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
    handleClickSelectedOption?: () => void;
    onOpen?: () => void;
    onClose?: () => void;
    onClear?: () => void;
    options?: ListValue[];
    loading?: boolean;
    stylingProps?: {
        optionProps?: SxProps<Theme>;
        textfieldInputProps?: {
            size?: TextFieldProps['size'];
            borderRadius?: string;
            customIcons?: {
                expand: JSX.Element;
                collapse: JSX.Element;
                clear: JSX.Element;
            };
        };
    };
}

const LISTBOX_HEIGHT = 300;
const THRESHOLD_TO_FIRE_PAGINATED_QUERY = 50;

export const LazyLoadingList = ({
    listId,
    listDataType,
    selectedId,
    onChange,
    error,
    helperText,
    disabled,
    handleClickSelectedOption,
    onOpen,
    onClose,
    onClear,
    ...props
}: LazyLoadingListProps) => {
    const {
        open,
        setOpen,
        inputValue,
        setInputValue,
        options: lazyLoadedOptions,
        value,
        setValue,
        allValues,
        isSearching,
        loadingOptimized,
        loadingPaginated,
        setPage,
        hasMore,
        setAllValues
    } = useLazyListValues(listId, listDataType, selectedId);

    const theme = useTheme();
    const scrollTimeoutRef = useRef<NodeJS.Timeout>();
    const isHeaderList = listId === -1;
    const loading = isHeaderList ? props.loading : loadingOptimized || loadingPaginated;

    const displayedOptions = useMemo(
        () => (loading ? [...lazyLoadedOptions, { id: '__loading__', value: 'Loading...' }] : lazyLoadedOptions),
        [lazyLoadedOptions, loading]
    );

    const memoizedInitialOptions = useMemo(() => props?.options, [props?.options]);

    const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
        if (isSearching || allValues || isHeaderList) return;
        const listboxNode = event.currentTarget;
        if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - THRESHOLD_TO_FIRE_PAGINATED_QUERY) {
            if (!loadingPaginated && hasMore && !isSearching && !allValues) {
                clearTimeout(scrollTimeoutRef.current);
                scrollTimeoutRef.current = setTimeout(() => {
                    setPage((prev) => prev + 1);
                }, 150); // 150ms debounce
            }
        }
    };

    const handleOpen = () => {
        // Always clear inputValue when opening dropdown to show all options (no filtering)
        // The actual selected value is preserved in the `value` prop
        hasUserTypedInSessionRef.current = false;
        setOpen(true);
        // Clear inputValue after a microtask to ensure it happens after MUI processes the open event
        setTimeout(() => {
            if (!hasUserTypedInSessionRef.current) {
                setInputValue('');
            }
        }, 0);
        onOpen?.();
    };

    const handleClose = () => {
        setOpen(false);
        // Restore inputValue to the selected value's label when closing (if value exists)
        // Restore if:
        // 1. User hasn't typed during this session, OR
        // 2. Input is empty and there's a value (user cleared their search)
        if (!hasUserTypedInSessionRef.current || (inputValue === '' && value)) {
            if (value) {
                const valueLabel = getOptionLabel(value);
                setInputValue(valueLabel);
                savedInputValueRef.current = valueLabel;
            } else {
                // If no value, keep inputValue empty
                setInputValue('');
                savedInputValueRef.current = '';
            }
        }
        // Reset the session typing flag for next time
        hasUserTypedInSessionRef.current = false;
        onClose?.();
    };

    const getOptionLabel = useCallback(
        (option: ListValue) => {
            if (listDataType === 'Object') return generateInlineObject(option?.dynamicObjectValue?.objectValues) || option?.value || '';
            if (listDataType === 'User')
                return option?.userValue?.name
                    ? `${option.userValue.enabled ? '' : '(Disabled) '}${option.userValue.name}`
                    : option?.value || '';

            // Base-field user dropdowns pass raw IUser options (top-level `email` + `enabled`,
            // no listDataType). Flag the currently-assigned but disabled user (OBD-870).
            const userOption = option as unknown as { email?: string; enabled?: boolean; name?: string };
            if (userOption?.email) {
                const displayName = userOption.name || option.value || '';
                return `${userOption.enabled === false ? '(Disabled) ' : ''}${displayName}`;
            }

            return option.value || option.name || '';
        },
        [listDataType]
    );

    useEffect(() => {
        if (isHeaderList) {
            setAllValues(props.options || []);
            const foundValue = memoizedInitialOptions?.find((opt) => opt.id === selectedId);
            if (foundValue) {
                setInputValue(getOptionLabel(foundValue));
                setValue(foundValue);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHeaderList, setAllValues, props?.options]);

    const previousSelectedIdRef = useRef<string | undefined>(undefined);
    const isUserTypingRef = useRef(false);
    const typingTimeoutRef = useRef<NodeJS.Timeout>();
    const savedInputValueRef = useRef<string>('');
    const hasUserTypedInSessionRef = useRef(false);

    // Cleanup timeout on unmount
    useEffect(
        () => () => {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
        },
        []
    );

    useEffect(() => {
        if (!selectedId || isHeaderList) return;
        // Only update if selectedId actually changed (not when user is typing)
        if (selectedId !== previousSelectedIdRef.current) {
            previousSelectedIdRef.current = selectedId;
            // First check memoizedInitialOptions, then check lazyLoadedOptions
            const foundValue =
                memoizedInitialOptions?.find((opt) => opt.id === selectedId) || lazyLoadedOptions?.find((opt) => opt.id === selectedId);
            if (foundValue) {
                setValue(foundValue);
                // Only set inputValue if dropdown is closed and user is not actively typing
                if (!open && !isUserTypingRef.current) {
                    const valueLabel = getOptionLabel(foundValue);
                    setInputValue(valueLabel);
                    savedInputValueRef.current = valueLabel;
                }
            }
        }
    }, [isHeaderList, setValue, setInputValue, memoizedInitialOptions, selectedId, lazyLoadedOptions, getOptionLabel, open]);

    // Set inputValue when value is set from selectedId (fallback for when value is set in hook)
    // Only if dropdown is closed, user is not actively typing and inputValue is empty or matches the value label
    useEffect(() => {
        if (!open && value && selectedId && value.id === selectedId && !isUserTypingRef.current) {
            const valueLabel = getOptionLabel(value);
            // Only set if inputValue is empty or matches the value label (user hasn't modified it)
            if (!inputValue || inputValue === valueLabel) {
                setInputValue(valueLabel);
                savedInputValueRef.current = valueLabel;
            }
        }
    }, [open, value, selectedId, inputValue, setInputValue, getOptionLabel]);

    // Clear inputValue when dropdown opens to show all options without filtering
    useEffect(() => {
        if (open && !hasUserTypedInSessionRef.current) {
            setInputValue('');
        }
    }, [open, setInputValue]);

    // Ensure inputValue shows the label when dropdown is closed and value exists
    useEffect(() => {
        if (!open && value && !isUserTypingRef.current) {
            const valueLabel = getOptionLabel(value);
            // Only update if inputValue doesn't match the value label
            if (inputValue !== valueLabel) {
                setInputValue(valueLabel);
                savedInputValueRef.current = valueLabel;
            }
        }
    }, [open, value, inputValue, setInputValue, getOptionLabel]);

    return (
        <Autocomplete
            open={open}
            value={value}
            disabled={disabled}
            onChange={(e, newValue, reason) => {
                if (reason === 'clear') {
                    onClear?.();
                    savedInputValueRef.current = '';
                }
                onChange(newValue?.id ?? '');
                setValue(newValue ?? null);
                if (newValue) {
                    const valueLabel = getOptionLabel(newValue);
                    setInputValue(valueLabel);
                    savedInputValueRef.current = valueLabel;
                } else {
                    setInputValue('');
                    savedInputValueRef.current = '';
                }
            }}
            onOpen={handleOpen}
            onClose={handleClose}
            options={(displayedOptions as ListValue[])?.filter(
                (option) =>
                    (option?.value || option?.name) &&
                    (listDataType !== 'Object' || (listDataType === 'Object' && option?.dynamicObjectValue))
            )}
            sx={{
                flexGrow: '1 !important',
                height: '100%',
                '& .MuiFormControl-root': { height: '100%' },
                '& .MuiInputBase-root': { height: '100%' }
            }}
            getOptionLabel={getOptionLabel}
            inputValue={inputValue}
            onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input') {
                    // Mark that user has typed during this session
                    hasUserTypedInSessionRef.current = true;
                    isUserTypingRef.current = true;
                    setInputValue(newInputValue);
                    // Clear any existing timeout
                    if (typingTimeoutRef.current) {
                        clearTimeout(typingTimeoutRef.current);
                    }
                    // Reset the typing flag after user stops typing
                    typingTimeoutRef.current = setTimeout(() => {
                        isUserTypingRef.current = false;
                    }, 300);
                } else if (reason === 'reset' || reason === 'clear') {
                    isUserTypingRef.current = false;
                    if (typingTimeoutRef.current) {
                        clearTimeout(typingTimeoutRef.current);
                    }
                }
            }}
            ListboxProps={{
                onScroll: handleScroll,
                style: { maxHeight: LISTBOX_HEIGHT, overflowY: 'auto', paddingBlock: 0 }
            }}
            renderInput={(params: AutocompleteRenderInputParams) => (
                <TextField
                    {...params}
                    variant="filled"
                    size={props.stylingProps?.textfieldInputProps?.size || 'small'}
                    fullWidth
                    disabled={disabled}
                    error={error}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        onClick: (e) => {
                            value && listDataType === 'Object' && handleClickSelectedOption?.();
                            params.inputProps?.onClick?.(e as any);
                        },
                        hiddenLabel: true,
                        disableUnderline: true,
                        placeholder: error ? 'Required Field' : '',
                        sx: {
                            borderRadius: props.stylingProps?.textfieldInputProps?.borderRadius,
                            backgroundColor: `${open ? '#EBEEFE' : '#f5f6f7'} !important`,
                            color: '#000',
                            padding: '0 0.8rem !important',
                            height: '2rem',
                            outline: error ? 'solid 1px #EF5350' : 'none',
                            '& .MuiInputBase-input': {
                                maxWidth: '55% !important',
                                overflow: 'hidden !important',
                                textOverflow: 'ellipsis !important',
                                whiteSpace: 'nowrap',
                                display: 'block',
                                backgroundColor: 'inherit !important',
                                color: 'inherit !important'
                            },
                            '& .MuiInputAdornment-root': {
                                '&:hover': { backgroundColor: theme.palette.secondary.light }
                            }
                        }
                    }}
                />
            )}
            popupIcon={
                // eslint-disable-next-line no-nested-ternary
                props.stylingProps?.textfieldInputProps?.customIcons
                    ? open
                        ? props.stylingProps.textfieldInputProps.customIcons.collapse
                        : props.stylingProps.textfieldInputProps.customIcons.expand
                    : undefined
            }
            clearIcon={props.stylingProps?.textfieldInputProps?.customIcons?.clear ?? undefined}
            renderOption={(_props, option, { selected }) => {
                const optionKey = option.id ?? option.value ?? option.name ?? Math.random().toString();
                if (option?.id === '__loading__') {
                    return (
                        <Box
                            component="li"
                            key={optionKey}
                            {..._props}
                            sx={{ display: 'flex !important', justifyContent: 'center !important', py: 1, backgroundColor: 'white' }}
                        >
                            <CircularProgress size={24} />
                        </Box>
                    );
                }

                const label = getOptionLabel(option);
                return (
                    <Box
                        component="li"
                        {..._props}
                        key={optionKey}
                        sx={{
                            border: 'solid 1px',
                            borderColor: theme.palette.primary[500],
                            height: '100%',
                            width: '100%',
                            color: selected ? theme.palette.primary.contrastText : '#54595E',
                            bgcolor: selected ? `${theme.palette.primary.dark} !important` : theme.palette.primary[300],
                            cursor: 'pointer',
                            '&:hover': {
                                bgcolor: `${theme.palette.primary.dark} !important`,
                                color: theme.palette.primary.contrastText
                            },
                            ...(props?.stylingProps?.optionProps || {})
                        }}
                    >
                        <Tooltip title={label} placement="top" arrow>
                            <Typography
                                noWrap
                                sx={{
                                    flex: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    px: 1
                                }}
                                data-user-enabled={String(option?.userValue?.enabled || false)}
                            >
                                {label}
                            </Typography>
                        </Tooltip>
                    </Box>
                );
            }}
        />
    );
};
